import { useEffect, useRef, useState } from 'react'
import {
  useFavoriteGrowthToolMutation,
  useLikeGrowthToolMutation,
  useUnfavoriteGrowthToolMutation,
  useUnlikeGrowthToolMutation,
} from '../api/growthTools'
import type { IGrowthToolResource } from '../api/types/growthTools'
import { profileApi } from '../api/profile'
import { useSettledToggleNetwork } from './useSettledToggleNetwork'

type GrowthToolEngagementSource = Pick<
  IGrowthToolResource,
  'id' | 'author_id' | 'slug' | 'viewer' | 'is_followed' | 'counters'
>

type UseGrowthToolEngagementOptions = {
  growthTool: GrowthToolEngagementSource
  cacheKey?: number | string
}

export function useGrowthToolEngagement({ growthTool, cacheKey }: UseGrowthToolEngagementOptions) {
  const { data: me } = profileApi.useGetProfileQuery()
  const [isFavorite, setIsFavorite] = useState(Boolean(growthTool.viewer?.is_followed ?? growthTool.is_followed))
  const [favoritesCount, setFavoritesCount] = useState(Number(growthTool.counters.subscribers ?? 0))
  const [isLiked, setIsLiked] = useState(Boolean(growthTool.viewer?.is_liked))
  const [likesCount, setLikesCount] = useState(Number(growthTool.counters.likes ?? 0))
  const [favoriteGrowthTool, { isLoading: isFavoriting }] = useFavoriteGrowthToolMutation()
  const [unfavoriteGrowthTool, { isLoading: isUnfavoriting }] = useUnfavoriteGrowthToolMutation()
  const [likeGrowthTool, { isLoading: isLiking }] = useLikeGrowthToolMutation()
  const [unlikeGrowthTool, { isLoading: isUnliking }] = useUnlikeGrowthToolMutation()
  const favoriteSettle = useSettledToggleNetwork()
  const likeSettle = useSettledToggleNetwork()
  const favoriteBurstRef = useRef<{ value: boolean; count: number } | null>(null)
  const likeBurstRef = useRef<{ value: boolean; count: number } | null>(null)

  useEffect(() => {
    setIsFavorite(Boolean(growthTool.viewer?.is_followed ?? growthTool.is_followed))
    setFavoritesCount(Number(growthTool.counters.subscribers ?? 0))
    setIsLiked(Boolean(growthTool.viewer?.is_liked))
    setLikesCount(Number(growthTool.counters.likes ?? 0))
  }, [growthTool])

  const toggleFavorite = () => {
    if (!favoriteBurstRef.current) {
      favoriteBurstRef.current = { value: isFavorite, count: favoritesCount }
    }
    const next = !isFavorite
    setIsFavorite(next)
    setFavoritesCount((n) => Math.max(0, n + (isFavorite ? -1 : 1)))

    favoriteSettle.schedule({
      desired: next,
      commit: async (desired) => {
        favoriteBurstRef.current = null
        if (desired) {
          await favoriteGrowthTool({ growthToolId: growthTool.id, cacheKey }).unwrap()
        } else {
          await unfavoriteGrowthTool({ growthToolId: growthTool.id, cacheKey }).unwrap()
        }
      },
      onSkip: () => {
        favoriteBurstRef.current = null
      },
      onError: () => {
        const burst = favoriteBurstRef.current
        favoriteBurstRef.current = null
        if (burst) {
          setIsFavorite(burst.value)
          setFavoritesCount(burst.count)
        }
      },
    })
  }

  const toggleLike = () => {
    if (!likeBurstRef.current) {
      likeBurstRef.current = { value: isLiked, count: likesCount }
    }
    const next = !isLiked
    setIsLiked(next)
    setLikesCount((n) => Math.max(0, n + (isLiked ? -1 : 1)))

    likeSettle.schedule({
      desired: next,
      commit: async (desired) => {
        likeBurstRef.current = null
        if (desired) {
          await likeGrowthTool({ growthToolId: growthTool.id, cacheKey }).unwrap()
        } else {
          await unlikeGrowthTool({ growthToolId: growthTool.id, cacheKey }).unwrap()
        }
      },
      onSkip: () => {
        likeBurstRef.current = null
      },
      onError: () => {
        const burst = likeBurstRef.current
        likeBurstRef.current = null
        if (burst) {
          setIsLiked(burst.value)
          setLikesCount(burst.count)
        }
      },
    })
  }

  return {
    isAuthor: Boolean(growthTool.viewer?.permissions?.can_edit_content) || me?.id === growthTool.author_id,
    isFavorite,
    favoritesCount,
    isLiked,
    likesCount,
    toggleFavorite,
    toggleLike,
    isFavoriteLoading: isFavoriting || isUnfavoriting,
    isLikeLoading: isLiking || isUnliking,
  }
}
